import React, { useEffect, useState } from "react";
import {
  DirectionsRenderer,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Images } from "../../assets";

const center = {
  lat: 38.7223,
  lng: -9.1393,
};

const GOOGLE_MAPS_APIKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
function MapComponent({
  commute,
  response,
}: {
  commute?: {
    from?: google.maps.LatLng;
    to?: google.maps.LatLng;
  };
  response?: (v: google.maps.DirectionsResult) => void;
}) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_APIKEY,
  });
  const [map, setMap] = React.useState<google.maps.Map | null>(null);

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    // This is just an example of getting and using the map instance!!! don't just blindly copy!
    // const bounds = new window.google.maps.LatLngBounds(center);
    // map.fitBounds(bounds);

    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);

  const [directionsResponse, setDirectionsResponse] =
    useState<google.maps.DirectionsResult | null>(null);

  const calculateRoute = () => {
    if (!commute?.from || !commute?.to || !map) {
      // alert("Please enter both From and To locations");
      return;
    }

    const service = new google.maps.DirectionsService();

    service.route(
      {
        origin: commute.from,
        destination: commute.to,
        travelMode: google.maps.TravelMode.DRIVING, // Other options: WALKING, BICYCLING, TRANSIT
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirectionsResponse(result);
          response && response(result);
        } else {
          console.error("Directions request failed due to " + status);
        }
      }
    );
  };

  useEffect(() => {
    calculateRoute();
  }, [commute?.from, commute?.to]);

  const polylineOptions = {
    strokeColor: "#000000", // Black line
    strokeOpacity: 1.0,
    strokeWeight: 3,
    geodesic: true,
    icons: [
      {
        icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeWeight: 2 },
        offset: "0",
        repeat: "10px",
      },
    ],
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={{ width: 500, height: 500, borderRadius: 10 }}
      center={center}
      zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{ styles: lightTheme }}
    >
      {/* Child components, such as markers, info windows, etc. */}
      <>
        {commute?.from && !commute.to && <Marker position={commute.from} />}
        {directionsResponse && (
          <DirectionsRenderer
            directions={directionsResponse}
            options={{
              polylineOptions: polylineOptions,
            }}
          />
        )}
      </>
    </GoogleMap>
  ) : (
    <>
      <img
        src={Images.placeholder_Image}
        alt="placeholder image"
        className="w-full max-w-[500px] h-auto"
      />
    </>
  );
}

export default React.memo(MapComponent);

// Custom map style for dark theme
const darkTheme = [
  {
    elementType: "geometry",
    stylers: [{ color: "#212121" }],
  },
  {
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [{ color: "#212121" }],
  },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#bdbdbd" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#181818" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#1b1b1b" }],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#2c2c2c" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8a8a8a" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#373737" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#3c3c3c" }],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [{ color: "#4e4e4e" }],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f2f2f" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#000000" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3d3d3d" }],
  },
];

const lightTheme = [
  {
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }], // Light grey
  },
  {
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    elementType: "labels.text.fill",
    stylers: [{ color: "#333333" }], // Dark grey
  },
  {
    elementType: "labels.text.stroke",
    stylers: [{ color: "#ffffff" }], // White background for text strokes
  },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#bdbdbd" }], // Light grey for admin borders
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }], // Medium grey for country labels
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4e4e4e" }], // Darker grey for city names
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }], // Medium grey for points of interest
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#e5e5e5" }], // Light grey for parks
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4e4e4e" }], // Dark grey for park labels
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#ffffff" }], // White background stroke
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }], // White roads
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4e4e4e" }], // Dark grey for road text
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#e0e0e0" }], // Light grey arterial roads
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#cccccc" }], // Light grey highways
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [{ color: "#d9d9d9" }], // Slightly darker for controlled access highways
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }], // Dark grey local roads
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#f0f0f0" }], // Very light grey for transit
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4e4e4e" }], // Dark grey for station labels
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c9c9c9" }], // Light grey water
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4e4e4e" }], // Dark grey for water labels
  },
];
